/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import {
  Award, Loader2, RefreshCw, TrendingUp, Upload, Users, BarChart3,
} from 'lucide-react';
import RapportIndividuel from './RapportIndividuel';
import DispatchBilansCard from './DispatchBilansCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const fcfa = (n) => `${(n || 0).toLocaleString('fr-FR')} FCFA`;

const scoreClass = (s) => {
  if (s == null) return 'bg-zinc-100 text-zinc-500 border-zinc-200';
  if (s >= 85) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s >= 70) return 'bg-orange-100 text-[#C45C26] border-orange-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const PodiumCard = ({ rank, row }) => {
  if (!row) return null;
  const medals = ['🥇', '🥈', '🥉'];
  const colors = [
    'from-yellow-50 to-yellow-100 border-yellow-300',
    'from-zinc-50 to-zinc-100 border-zinc-300',
    'from-orange-50 to-orange-100 border-orange-300',
  ];
  return (
    <Card
      className={`bg-gradient-to-br ${colors[rank]} border-2`}
      data-testid={`podium-${rank + 1}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{medals[rank]}</div>
          <div className="flex-1">
            <p className="font-serif text-lg font-semibold text-zinc-900">{row.agent_name}</p>
            <p className="text-xs text-zinc-600">
              Performance {row.performance ?? '—'} · Rigueur {row.rigueur ?? '—'} · Discipline {row.discipline ?? '—'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-mono font-bold text-zinc-900">{row.score ?? '—'}</div>
            <div className="text-xs text-zinc-500">/100</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ImportCard = ({ title, subtitle, accept, onUpload, testId }) => {
  const [busy, setBusy] = useState(false);
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await onUpload(file); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <h3 className="font-semibold text-zinc-900 text-sm">{title}</h3>
      <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
      <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer text-[#1B4D3E] hover:underline">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        Choisir le fichier {accept}
        <input type="file" accept={accept} className="hidden" onChange={handleChange} disabled={busy} data-testid={testId} />
      </label>
    </div>
  );
};

const AgentPerformanceTab = () => {
  const todayPeriod = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [view, setView] = useState('classement'); // 'classement' | 'rapport'
  const [period, setPeriod] = useState(todayPeriod);
  const [rows, setRows] = useState([]);
  const [hero, setHero] = useState(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  // Lecture du user courant pour la vue rapport (filtrage agent vs admin/manager)
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('dermascan_user') || '{}'); }
    catch { return {}; }
  }, []);
  const canSelectAgent = !!(
    currentUser.is_admin
    || currentUser.role === 'admin'
    || currentUser.role === 'manager'
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [lb, h, im] = await Promise.all([
        axios.get(`${API}/agent-performance/leaderboard?period=${period}`, { headers: auth() }),
        axios.get(`${API}/agent-performance/hero-of-month?period=${period}`, { headers: auth() }),
        axios.get(`${API}/agent-performance/imports`, { headers: auth() }),
      ]);
      setRows(lb.data.rows || []);
      setHero(h.data.hero || null);
      setImports(im.data || []);
    } catch (e) { toast.error('Erreur chargement performance'); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { reload(); }, [reload]);

  const onUploadCash = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/agent-performance/import/cash-discrepancies`, fd, { headers: auth() });
      toast.success(`${r.data.rows_upserted}/${r.data.rows_parsed} ligne(s) importées (caisse)`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import caisse'); }
  };
  const onUploadPunc = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/agent-performance/import/punctuality`, fd, { headers: auth() });
      toast.success(`${r.data.rows_upserted}/${r.data.rows_parsed} retard(s) importés`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import retards'); }
  };

  const openDetail = async (agentId) => {
    setSelected(agentId);
    try {
      const r = await axios.get(`${API}/agent-performance/agent/${agentId}?period=${period}`, { headers: auth() });
      setDetail(r.data);
    } catch (e) { toast.error('Erreur détail agent'); }
  };

  return (
    <div className="space-y-6" data-testid="agent-performance-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-serif font-semibold text-zinc-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-[#C45C26]" /> Pilotage performance agents
          </h2>
          <p className="text-sm text-[#5D4E37]">
            Score multi-modules · Discipline (≤25%) + Commercial + Réceptions + Inventaires + Conversion + Fidélisation · Héros du mois automatique
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-sm"
            data-testid="period-input"
          />
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recharger
          </Button>
        </div>
      </div>

      {/* Sub-nav tabs */}
      <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-1" data-testid="ap-sub-tabs">
        <button
          onClick={() => setView('classement')}
          className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition ${
            view === 'classement'
              ? 'bg-white text-zinc-900 shadow-sm font-medium'
              : 'text-zinc-600 hover:text-zinc-900'
          }`}
          data-testid="ap-tab-classement"
        >
          <Award className="w-4 h-4" /> Classement & héros
        </button>
        <button
          onClick={() => setView('rapport')}
          className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition ${
            view === 'rapport'
              ? 'bg-white text-zinc-900 shadow-sm font-medium'
              : 'text-zinc-600 hover:text-zinc-900'
          }`}
          data-testid="ap-tab-rapport"
        >
          <BarChart3 className="w-4 h-4" /> Rapport individuel
        </button>
      </div>

      {view === 'rapport' && (
        <>
          {canSelectAgent && <DispatchBilansCard />}
          <RapportIndividuel
            canSelectAgent={canSelectAgent}
            currentUserId={currentUser.id}
          />
        </>
      )}

      {view === 'classement' && (
        <>
        {/* Hero card */}
      {hero ? (
        <Card className="bg-gradient-to-br from-[#1B4D3E] to-[#0F3D32] text-white border-0" data-testid="hero-card">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="text-5xl">🏆</div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-emerald-200">Héros du mois · {period}</p>
              <p className="text-2xl font-serif font-bold mt-1">{hero.agent_name}</p>
              <p className="text-sm text-emerald-100 mt-0.5">
                Commercial {hero.performance ?? '—'} · Rigueur {hero.rigueur ?? '—'} · Discipline {hero.discipline ?? '—'}
              </p>
              {hero.partial_evaluation && (
                <p className="text-[11px] text-amber-200 mt-1 italic">
                  ⚠ Évaluation partielle — score basé sur peu de modules
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-5xl font-mono font-bold">{hero.score}</div>
              <div className="text-xs text-emerald-100">/100</div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-5 text-center text-sm text-zinc-500">
            Pas encore de données pour {period}. Importez les fichiers Mediciel ci-dessous, ou aucun agent ne remplit les critères de performance ce mois-ci.
          </CardContent>
        </Card>
      )}

      {/* Podium top 3 */}
      <div className="grid md:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => <PodiumCard key={i} rank={i} row={rows[i]} />)}
      </div>

      {/* Full leaderboard */}
      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
          <Users className="w-4 h-4 text-zinc-500" />
          <h3 className="font-semibold text-zinc-900 text-sm">Classement complet · {period}</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-3 py-2 font-medium">#</th>
              <th className="px-3 py-2 font-medium">Agent</th>
              <th className="px-3 py-2 font-medium text-right">Commercial</th>
              <th className="px-3 py-2 font-medium text-right">Rigueur</th>
              <th className="px-3 py-2 font-medium text-right">Discipline</th>
              <th className="px-3 py-2 font-medium text-right">Score</th>
              <th className="px-3 py-2 font-medium text-right">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.agent_id}
                className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${r.excluded_from_ranking ? 'opacity-40' : ''}`}
                onClick={() => openDetail(r.agent_id)}
                data-testid={`leaderboard-row-${r.agent_id}`}
              >
                <td className="px-3 py-2 font-mono">{r.rank}</td>
                <td className="px-3 py-2 font-medium text-zinc-900">
                  {r.agent_name}
                  {r.partial_evaluation && (
                    <span className="ml-1.5 text-[10px] text-amber-700">⚠ partiel</span>
                  )}
                  {r.discipline_capped && (
                    <span className="ml-1 text-[10px] text-zinc-500" title="Discipline plafonnée à 25% du score global">⊘</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{r.performance ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{r.rigueur ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{r.discipline ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  <Badge className={`${scoreClass(r.score)} border font-mono`}>{r.score ?? '—'}/100</Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === 'performant' && <span className="text-[10px] text-emerald-700">🌟 Performant</span>}
                  {r.status === 'a_suivre' && <span className="text-[10px] text-amber-700">À suivre</span>}
                  {r.status === 'a_accompagner' && <span className="text-[10px] text-red-700">À accompagner</span>}
                  {!r.status && <span className="text-[10px] text-zinc-400">—</span>}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-zinc-500">
                Aucune donnée pour {period}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Imports */}
      <div className="grid md:grid-cols-2 gap-3">
        <ImportCard
          title="Importer les écarts de caisse Mediciel"
          subtitle="Fichier Excel mensuel — colonnes Date / Différence par agent."
          accept=".xlsx"
          onUpload={onUploadCash}
          testId="import-cash-input"
        />
        <ImportCard
          title="Importer le pointage retards Mediciel"
          subtitle="Fichier RTF mensuel — état des retards constatés."
          accept=".rtf"
          onUpload={onUploadPunc}
          testId="import-punc-input"
        />
      </div>

      {/* Imports history */}
      {imports.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <h3 className="font-semibold text-zinc-900 text-sm mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Historique des imports
          </h3>
          <table className="w-full text-xs">
            <thead className="text-zinc-500"><tr>
              <th className="text-left py-1">Date</th><th className="text-left py-1">Type</th>
              <th className="text-left py-1">Fichier</th><th className="text-right py-1">Lignes</th>
            </tr></thead>
            <tbody>
              {imports.slice(0, 10).map((i) => (
                <tr key={i.id} className="border-t border-zinc-100">
                  <td className="py-1 font-mono">{(i.imported_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1">{i.type === 'cash_discrepancies' ? 'Caisse' : 'Retards'}</td>
                  <td className="py-1 truncate max-w-[200px]" title={i.filename}>{i.filename}</td>
                  <td className="py-1 text-right font-mono">{i.rows_upserted}/{i.rows_parsed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {/* Agent detail modal */}
      {selected && detail && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
          onClick={() => { setSelected(null); setDetail(null); }}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="agent-detail-modal"
          >
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-zinc-200">
              <h3 className="text-lg font-serif font-semibold">Détail score · {period}</h3>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-zinc-500 hover:text-zinc-900 text-xl">×</button>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
              <div className="grid grid-cols-4 gap-2 mb-4">
                {['performance', 'rigueur', 'discipline', 'score'].map((k) => (
                  <div key={k} className="rounded-lg border border-zinc-200 p-3 text-center">
                    <div className="text-xs uppercase text-zinc-500">{k}</div>
                    <div className="text-2xl font-mono font-bold mt-1">{detail.monthly[k] ?? '—'}</div>
                  </div>
                ))}
              </div>
              <h4 className="text-sm font-semibold mb-2">Évolution quotidienne ({detail.monthly.days_evaluated}j)</h4>
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-500 border-b">
                  <th className="text-left py-1">Date</th>
                  <th className="text-right py-1">Perf</th>
                  <th className="text-right py-1">Rig</th>
                  <th className="text-right py-1">Disc</th>
                  <th className="text-right py-1">Score</th>
                </tr></thead>
                <tbody>
                  {detail.daily_scores.map((d) => (
                    <tr key={d.date} className="border-b border-zinc-100">
                      <td className="py-1 font-mono">{d.date}</td>
                      <td className="py-1 text-right font-mono">{d.performance}</td>
                      <td className="py-1 text-right font-mono">{d.rigueur}</td>
                      <td className="py-1 text-right font-mono">{d.discipline}</td>
                      <td className="py-1 text-right font-mono"><Badge className={`${scoreClass(d.score)} border font-mono text-xs`}>{d.score}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPerformanceTab;
